import { mapWithKeys } from '@/helpers';
import { _isEmpty, _notNil } from '@/littledash';
import store from '@/store';
import { DateRenderFormat, DateUtils } from '@/utils/Date.utils';
import { add, sub } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import type { Task, TaskTarget } from '@/model/Task.model';
import type { Study } from '@/model/Study.model';
import type { ID } from '@/model/Common.model';

export const getAllStudyIdsFromTasks = (tasks: Array<Task>): Array<ID> => {
  const studyIds: Array<ID> = [];
  if (!_isEmpty(tasks) && Array.isArray(tasks)) {
    tasks.map((task) => {
      return !studyIds.includes(task.study.id) && studyIds.push(task.study.id);
    });
  }

  return studyIds;
};

export const groupTasksByStudyMap = (tasks: Array<Task>): Record<string, Array<Task>> => {
  let groupedTasks: Record<string, Array<Task>> = {};
  if (!_isEmpty(tasks) && Array.isArray(tasks)) {
    const studyIds = getAllStudyIdsFromTasks(tasks);
    if (!_isEmpty(studyIds) && Array.isArray(studyIds)) {
      groupedTasks = mapWithKeys(studyIds, (id: ID) => {
        const tasksAssosiated = tasks.filter((t) => t.study.id === id);
        return { [String(id)]: [...tasksAssosiated] };
      });
    }
  }

  return groupedTasks;
};

export const filterStudy = (studies: Array<Study>, id: ID): Study | undefined => {
  if (!_isEmpty(studies) && Array.isArray(studies) && _notNil(id)) {
    return studies.find((s) => s.id === id);
  }

  return undefined;
};

export const hasPendingTasks = (tasks: Array<Task>): boolean => {
  const dateNow = DateUtils.dateNow();
  return tasks.some((task) => (task as any).date === dateNow && task.status !== 'complete');
};

export const togglePendingTasksNotification = (pendingTasksForToday: boolean): void => {
  store.dispatch({
    type: 'PENDING_TASKS_FOR_TODAY',
    pendingTasksForToday,
  });
};

export const nextDate = (currentDate: string): string => {
  if (DateUtils.isValidDate(currentDate)) {
    const nextDate = add(fromZonedTime(currentDate, 'UTC'), { days: 1 });
    return formatInTimeZone(nextDate, 'UTC', DateRenderFormat.ISODate);
  }
  return 'Invalid date';
};

export const prevDate = (currentDate: string): string => {
  if (DateUtils.isValidDate(currentDate)) {
    const prevDate = sub(fromZonedTime(currentDate, 'UTC'), { days: 1 });
    return formatInTimeZone(prevDate, 'UTC', DateRenderFormat.ISODate);
  }
  return 'Invalid date';
};

export const getTargetNameForTasks = (target: TaskTarget): string => {
  if (_notNil(target)) {
    if (target.type === 'animal') {
      return 'All animals';
    }
    if (target.type === 'group' && Array.isArray(target.groups)) {
      const groupSize = target.groups.length;
      return groupSize === 1 ? `${groupSize} group` : `${groupSize} groups`;
    }
  }
  return '-';
};
